import { Trans, t } from '@lingui/macro';
import { useActiveLocale } from 'hooks/useActiveLocale';
import { useSnackbar } from 'notistack';
import { LoadingButton } from '@mui/lab';
import {
  Box,
  Chip,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Typography,
} from '@mui/material';
import { Controller, useForm } from 'react-hook-form';
import { useMutation, useQuery } from 'react-query';
import { categoryService, enterpriseSetupService, queryClient } from 'services';
import { LaborTermType } from 'types/Category';
import { EnterpriseSettingType, EnterpriseSettingUpdateType } from 'types/EnterpriseSetup';

const EnterpriseFullTime = ({ setting }: { setting?: EnterpriseSettingType }) => {
  const userLocale = useActiveLocale();
  const { enqueueSnackbar } = useSnackbar();
  const { control, handleSubmit } = useForm();

  const { data: laborTerms } = useQuery(['fetchLaborTerms'], () => categoryService.fetchLaborTerms());

  const { mutate: createFullTimeLabors, isLoading } = useMutation(enterpriseSetupService.createFullTimeLabors, {
    onSuccess: () => {
      enqueueSnackbar(t`Successful`);
      queryClient.invalidateQueries('enterpriseSetupService.getGeneralSettings');
    },
  });

  const handleClickSubmit = () => {
    handleSubmit((values: EnterpriseSettingUpdateType) => {
      createFullTimeLabors({ ...values, id: setting?.enterpriseId! });
    })();
  };

  return (
    <div>
      <Typography variant='h4' className='mt-4'>
        <Trans>Eligible Condition for Future Advance</Trans>
      </Typography>

      <div className='flex items-center gap-4'>
        <TableContainer component={Paper} variant='outlined'>
          <Table>
            <TableBody>
              <TableRow>
                <TableCell>
                  <Trans>Type</Trans>
                </TableCell>
                <TableCell colSpan={2}>
                  <Trans>Full Time</Trans>
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell>
                  <Trans>Type of Labor Contract</Trans>
                </TableCell>
                <TableCell colSpan={2}>
                  <Controller
                    name='laborTermIds'
                    defaultValue={setting?.laborTermIds}
                    control={control}
                    rules={{ required: true }}
                    render={({ field, fieldState: { invalid, error } }) => (
                      <Select
                        {...field}
                        multiple
                        fullWidth
                        error={invalid}
                        renderValue={(selected) => (
                          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                            {selected
                              .map((value: number) => laborTerms?.find((item) => item.id === value))
                              .filter((item: any) => item)
                              .map((item: LaborTermType) => (
                                <Chip key={item.id} label={userLocale === 'vi-VN' ? item.viContent : item.enContent} />
                              ))}
                          </Box>
                        )}
                      >
                        {laborTerms?.map((item) => (
                          <MenuItem key={item.id} value={item.id}>
                            {item.enContent}
                          </MenuItem>
                        ))}
                      </Select>
                    )}
                  />
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>

        <LoadingButton variant='contained' loading={isLoading} onClick={handleClickSubmit} className='w-32'>
          <Trans>Apply</Trans>
        </LoadingButton>
      </div>
    </div>
  );
};

export default EnterpriseFullTime;
